import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Order, orderService } from '../services/orderService';
import { authService } from '../services/authService';

// Extend OrderStatus type to include 'distributed'
type OrderStatus = Order['status'] | 'distributed';

const OrderCard = ({ order, onStatusChange }: { order: Order; onStatusChange: (orderId: string, status: OrderStatus) => void }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const customerName = `${order.customer_info.firstName} ${order.customer_info.lastName}`;
  const orderDate = new Date(order.date);
  const expirationDate = new Date(order.expiration_date);
  const cancellationDate = new Date(order.cancellation_date);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* En-tête de la carte */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className={`w-3 h-3 rounded-full ${
              order.status === 'pending' ? 'bg-yellow-400' :
              order.status === 'paid' ? 'bg-green-400' :
              order.status === 'distributed' ? 'bg-blue-400' :
              'bg-red-400'
            }`} />
            <div>
              <h3 className="font-semibold">Commande #{order.id.slice(0, 8)}</h3>
              <p className="text-sm text-gray-500">{customerName}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-lg font-semibold">{order.total.toFixed(2)}€</span>
            <span className={`px-2 py-1 rounded-full text-xs ${
              order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              order.status === 'paid' ? 'bg-green-100 text-green-800' :
              order.status === 'distributed' ? 'bg-blue-100 text-blue-800' :
              'bg-red-100 text-red-800'
            }`}>
              {order.status === 'pending' ? 'En attente' :
               order.status === 'paid' ? 'Payée' :
               order.status === 'distributed' ? 'Distribuée' :
               'Annulée'}
            </span>
            <svg 
              className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Contenu dépliable */}
      {isExpanded && (
        <div className="border-t p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Date de commande</p>
              <p>{orderDate.toLocaleDateString('fr-FR')}</p>
            </div>
            <div>
              <p className="text-gray-500">Méthode de paiement</p>
              <p>{order.payment_method}</p>
            </div>
            <div>
              <p className="text-gray-500">Expiration</p>
              <p>{expirationDate.toLocaleDateString('fr-FR')}</p>
            </div>
            <div>
              <p className="text-gray-500">Annulation possible jusqu'au</p>
              <p>{cancellationDate.toLocaleDateString('fr-FR')}</p>
            </div>
            <div>
              <p className="text-gray-500">Numéro de téléphone</p>
              <p>{order.customer_info.phoneNumber}</p>
            </div>
            {order.customer_info.isFaidherbard && order.customer_info.class && (
              <div>
                <p className="text-gray-500">Classe</p>
                <p>{order.customer_info.class}</p>
              </div>
            )}
            {order.customer_info.tshirtSize && (
              <div>
                <p className="text-gray-500">Taille du T-shirt</p>
                <p>{order.customer_info.tshirtSize}</p>
              </div>
            )}
          </div>

          <div>
            <h4 className="font-semibold mb-2">Articles</h4>
            <ul className="space-y-2">
              {order.items.map((item) => (
                <li key={item.id} className="flex justify-between text-sm">
                  <span>{item.name} x {item.quantity}</span>
                  <span>{(item.price * item.quantity).toFixed(2)}€</span>
                </li>
              ))}
            </ul>
          </div>

          {order.status === 'pending' && (
            <div className="flex space-x-2 pt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(order.id, 'paid');
                }}
                className="flex-1 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                Marquer comme payée
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(order.id, 'cancelled');
                }}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Annuler
              </button>
            </div>
          )}
          {order.status === 'paid' && (
            <div className="pt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(order.id, 'distributed');
                }}
                className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Marquer comme distribuée
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon }: { title: string; value: string | number; icon: string }) => (
  <div className="bg-white rounded-xl shadow-sm p-6">
    <div className="flex items-center space-x-4">
      <div className="p-3 bg-pink-100 rounded-lg">
        <span className="text-2xl">{icon}</span>
      </div>
      <div>
        <h3 className="text-gray-500 text-sm">{title}</h3>
        <p className="text-2xl font-bold">{value}</p>
      </div>
    </div>
  </div>
);

export const AdminDashboard = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'paid' | 'cancelled' | 'distributed'>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<string>('all');
  const [priceRange, setPriceRange] = useState<string>('all');
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [availableClasses, setAvailableClasses] = useState<string[]>([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    paidOrders: 0,
    cancelledOrders: 0,
    distributedOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!authService.isLoggedIn()) {
      navigate('/admin/login');
      return;
    }

    const fetchOrders = async () => {
      try {
        setLoading(true);
        const allOrders = await orderService.getAllOrders();
        setOrders(allOrders);

        // Extract unique classes for the filter
        const uniqueClasses = Array.from(
          new Set(allOrders.map(o => o.customer_info.class).filter(c => c))
        ).sort();
        setAvailableClasses(uniqueClasses as string[]);

        // Calculer les statistiques
        const totalOrders = allOrders.length;
        const pendingOrders = allOrders.filter(o => o.status === 'pending').length;
        const paidOrders = allOrders.filter(o => o.status === 'paid').length;
        const cancelledOrders = allOrders.filter(o => o.status === 'cancelled').length;
        const distributedOrders = allOrders.filter(o => o.status === 'distributed').length;
        const totalRevenue = allOrders
          .filter(o => o.status === 'paid' || o.status === 'distributed')
          .reduce((sum, order) => sum + order.total, 0);
        const averageOrderValue = (paidOrders + distributedOrders) > 0 ? totalRevenue / (paidOrders + distributedOrders) : 0;

        setStats({
          totalOrders,
          pendingOrders,
          paidOrders,
          cancelledOrders,
          distributedOrders,
          totalRevenue,
          averageOrderValue
        });
      } catch (err) {
        setError('Erreur lors du chargement des commandes');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [navigate]);

  const handleStatusChange = async (orderId: string, status: OrderStatus) => {
    try {
      await orderService.updateOrderStatus(orderId, status);
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status } : order
      ));
    } catch (err) {
      setError('Erreur lors de la mise à jour du statut');
      console.error(err);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/admin/login');
  };

  const filteredOrders = orders
    .filter(order => order.status === activeTab)
    .filter(order => {
      if (!searchQuery) return true;
      const customerName = `${order.customer_info.firstName} ${order.customer_info.lastName}`.toLowerCase();
      const orderIdMatch = order.id.toLowerCase().includes(searchQuery.toLowerCase());
      return customerName.includes(searchQuery.toLowerCase()) || orderIdMatch;
    })
    .filter(order => {
      if (paymentMethod === 'all') return true;
      return order.payment_method === paymentMethod;
    })
    .filter(order => {
      if (priceRange === 'all') return true;
      const price = order.total;
      switch (priceRange) {
        case '0-10':
          return price <= 10;
        case '10-20':
          return price > 10 && price <= 20;
        case '20-30':
          return price > 20 && price <= 30;
        case '30-40':
          return price > 30 && price <= 40;
        case '40+':
          return price > 40;
        default:
          return true;
      }
    })
    .filter(order => {
      if (selectedClass === 'all') return true;
      return order.customer_info.class === selectedClass && order.customer_info.isFaidherbard;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Tableau de bord</h1>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/admin/products')}
              className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            >
              Gérer les produits
            </button>
            <button
              onClick={() => navigate('/admin/analytics')}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Analyses avancées
            </button>
            <button
              onClick={() => navigate('/admin/drink-calculator')}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
            >
              Calculateur Boissons
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Déconnexion
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatCard 
            title="Commandes totales" 
            value={stats.totalOrders} 
            icon="📦"
          />
          <StatCard 
            title="Commandes en attente" 
            value={stats.pendingOrders} 
            icon="⏳"
          />
          <StatCard 
            title="Commandes payées" 
            value={stats.paidOrders} 
            icon="✅"
          />
          <StatCard 
            title="Commandes distribuées"
            value={stats.distributedOrders}
            icon="🚚"
          />
          <StatCard 
            title="Commandes annulées" 
            value={stats.cancelledOrders} 
            icon="❌"
          />
          <StatCard 
            title="Chiffre d'affaires" 
            value={`${stats.totalRevenue.toFixed(2)}€`} 
            icon="💰"
          />
          <StatCard 
            title="Panier moyen" 
            value={`${stats.averageOrderValue.toFixed(2)}€`} 
            icon="🛒"
          />
        </div>

        {/* Barre de recherche et filtres */}
        <div className="flex flex-col space-y-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher une commande par nom..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('pending')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'pending'
                    ? 'bg-pink-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                En attente
              </button>
              <button
                onClick={() => setActiveTab('paid')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'paid'
                    ? 'bg-pink-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Payées
              </button>
              <button
                onClick={() => setActiveTab('distributed')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'distributed'
                    ? 'bg-pink-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Distribuées
              </button>
              <button
                onClick={() => setActiveTab('cancelled')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'cancelled'
                    ? 'bg-pink-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Annulées
              </button>
            </div>
          </div>

          {/* Nouveaux filtres */}
          <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="all">Toutes les méthodes de paiement</option>
                <option value="cash">Espèces</option>
                <option value="rib">Virement</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>
            <div className="flex-1">
              <select
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="all">Tous les prix</option>
                <option value="0-10">0€ - 10€</option>
                <option value="10-20">10€ - 20€</option>
                <option value="20-30">20€ - 30€</option>
                <option value="30-40">30€ - 40€</option>
                <option value="40+">40€ et plus</option>
              </select>
            </div>
            <div className="flex-1">
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="all">Toutes les classes</option>
                {availableClasses.map(className => (
                  <option key={className} value={className}>{className}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Liste des commandes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchQuery 
                ? `Aucune commande ${
                    activeTab === 'pending' ? 'en attente' :
                    activeTab === 'paid' ? 'payée' :
                    activeTab === 'distributed' ? 'distribuée' :
                    'annulée'
                  } ne correspond à votre recherche`
                : `Aucune commande ${
                    activeTab === 'pending' ? 'en attente' :
                    activeTab === 'paid' ? 'payée' :
                    activeTab === 'distributed' ? 'distribuée' :
                    'annulée'
                  }`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}; 